import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  createCoreNumbering,
  deleteCoreNumbering,
  listCoreNumbering,
  NUMBERING_ENTITY_TYPES,
  previewNumber,
  RESET_FREQUENCIES,
  resetFrequencyMeta,
  updateCoreNumbering,
  type CoreNumbering,
} from '@/lib/coreSystem';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

interface FormState {
  id: number | null;
  entityType: string;
  label: string;
  prefix: string;
  padding: number;
  resetFrequency: string;
  useYear: boolean;
}
const EMPTY: FormState = { id: null, entityType: 'invoice', label: '', prefix: 'FAC', padding: 5, resetFrequency: 'yearly', useYear: true };

function entityMeta(key: string) {
  return NUMBERING_ENTITY_TYPES.find((e) => e.key === key) || NUMBERING_ENTITY_TYPES[0];
}

export default function NumberingTab({ org }: Props) {
  const [configs, setConfigs] = useState<CoreNumbering[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setConfigs(await listCoreNumbering(org.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setOpen(true); };
  const openEdit = (c: CoreNumbering) => {
    setForm({
      id: c.id, entityType: c.entity_type, label: c.label || '', prefix: c.prefix,
      padding: c.padding ?? 5, resetFrequency: c.reset_frequency || 'yearly', useYear: c.year != null,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.prefix.trim()) return;
    setSaving(true);
    try {
      if (form.id) {
        await updateCoreNumbering(form.id, {
          prefix: form.prefix.trim().toUpperCase(), padding: form.padding,
          resetFrequency: form.resetFrequency, label: form.label || undefined, useYear: form.useYear,
        });
      } else {
        await createCoreNumbering({
          tenantId: org.tenant_id, organizationId: org.id, entityType: form.entityType,
          label: form.label || undefined, prefix: form.prefix.trim().toUpperCase(),
          padding: form.padding, resetFrequency: form.resetFrequency, useYear: form.useYear,
        });
      }
      setOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c: CoreNumbering) => {
    setBusyId(c.id);
    try { await deleteCoreNumbering(c.id); load(); } finally { setBusyId(null); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <p className="text-xs text-foreground-500 max-w-2xl">
          Numérotation centrale : chaque entreprise configure ses préfixes (DEV, FAC, CMD, RES…),
          le <span className="font-semibold text-foreground-700">rembourrage</span> (padding) et la fréquence de réinitialisation.
        </p>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-add-line"></i> Nouvelle numérotation
        </button>
      </div>

      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : configs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-hashtag text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucune numérotation configurée.</p>
          <p className="text-xs text-foreground-400 mt-1">Exemple : facture → FAC-2026-00001.</p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-background-100 text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
            <span className="col-span-3">Type</span>
            <span className="col-span-3">Prochain numéro</span>
            <span className="col-span-2">Padding</span>
            <span className="col-span-2">Reset</span>
            <span className="col-span-2 text-right">Actions</span>
          </div>
          {configs.map((c) => {
            const meta = entityMeta(c.entity_type);
            return (
              <div key={c.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-background-200/60 items-center">
                <div className="col-span-3 flex items-center gap-2 min-w-0">
                  <span className="w-7 h-7 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center"><i className={meta.icon}></i></span>
                  <span className="text-xs font-medium text-foreground-800 truncate">{c.label || meta.label}</span>
                </div>
                <span className="col-span-3 text-xs font-semibold text-primary-700 tabular-nums truncate">{previewNumber(c)}</span>
                <span className="col-span-2 text-xs text-foreground-600">{c.padding}</span>
                <span className="col-span-2 text-xs text-foreground-600">{resetFrequencyMeta(c.reset_frequency).label}</span>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <button onClick={() => openEdit(c)} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"><i className="ri-edit-line"></i></button>
                  <button onClick={() => remove(c)} disabled={busyId === c.id} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-bin-line"></i></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal
          title={form.id ? 'Modifier la numérotation' : 'Nouvelle numérotation'}
          onClose={() => setOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={save} disabled={saving || !form.prefix.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            {!form.id && (
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Type d'entité</span>
                <select className={INPUT} value={form.entityType} onChange={(e) => { const m = entityMeta(e.target.value); setForm({ ...form, entityType: e.target.value, prefix: m.prefix }); }}>
                  {NUMBERING_ENTITY_TYPES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                </select>
              </label>
            )}
            <div className="grid grid-cols-3 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Préfixe</span>
                <input className={INPUT} value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value.toUpperCase() })} placeholder="FAC" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Padding</span>
                <input type="number" min={1} max={10} className={INPUT} value={form.padding} onChange={(e) => setForm({ ...form, padding: Math.max(1, Math.min(10, Number(e.target.value) || 1)) })} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Reset</span>
                <select className={INPUT} value={form.resetFrequency} onChange={(e) => setForm({ ...form, resetFrequency: e.target.value })}>
                  {RESET_FREQUENCIES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Libellé (option)</span>
              <input className={INPUT} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex : Factures de vente" />
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.useYear} onChange={(e) => setForm({ ...form, useYear: e.target.checked })} className="w-4 h-4 rounded accent-primary-500" />
              <span className="text-xs text-foreground-600">Inclure l'année dans le numéro</span>
            </label>
            <div className="p-3 bg-background-100 rounded-md">
              <p className="text-[11px] text-foreground-500 mb-1">Aperçu du prochain numéro</p>
              <p className="text-sm font-semibold text-primary-700 tabular-nums">{previewNumber({ prefix: form.prefix, sequence: 0, padding: form.padding, year: form.useYear ? new Date().getFullYear() : null })}</p>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}